import { graphql } from '@/gql/codegen/gql';
import { CourseLessonContentTypeEnum } from '@/gql/codegen/graphql';
import { useAmplitudeLogEvent } from '@/routes/-hooks/useAmplitudeLogEvent';
import { GET_USER_ATTENDANCE } from '@/routes/_embaUserLayout/user-attendances/$userAttendanceSlug/_lessonSideBarLayout';
import { useMutation } from '@apollo/client';
import { useNavigate } from '@tanstack/react-router';

export const START_COURSE_LESSON_MUTAION = graphql(/* GraphQL */ `
  mutation EmbaStartCourseLesson($input: StartCourseLessonMutationInput!) {
    embaStartCourseLesson(input: $input) {
      success
    }
  }
`);

export const COMPLETE_COURSE_LESSON_MUTAION = graphql(/* GraphQL */ `
  mutation EmbaCompleteCourseLesson(
    $input: CompleteCourseLessonMutationInput!
  ) {
    embaCompleteCourseLesson(input: $input) {
      success
    }
  }
`);

export const useCourseLessonProgressMutations = (
  userAttendanceSlug: string,
  lessonSlug: string,
  lessonContentType: CourseLessonContentTypeEnum,
) => {
  const navigate = useNavigate();

  const trackAmplitudeLogEvent = useAmplitudeLogEvent();

  const [
    embaStartCourseLessonMutation,
    { error: startCourseLessonError, loading: startCourseLessonLoading },
  ] = useMutation(START_COURSE_LESSON_MUTAION, {
    refetchQueries: [
      {
        query: GET_USER_ATTENDANCE,
        variables: {
          embaUserAttendanceSlug: userAttendanceSlug,
          embaCourseLessonSlug: lessonSlug,
        },
      },
    ],
  });

  const onEmbaStartCourseLesson = async () => {
    await embaStartCourseLessonMutation({
      variables: {
        input: {
          embaUserAttendanceSlug: userAttendanceSlug,
          embaCourseLessonSlug: lessonSlug,
        },
      },
    });

    trackAmplitudeLogEvent('lessonStarted', {
      embaUserAttendanceSlug: userAttendanceSlug,
      lessonSlug: lessonSlug,
    });
  };

  const [
    embaCompleteCourseLessonMutation,
    { error: completeCourseLessonError, loading: completeCourseLessonLoading },
  ] = useMutation(COMPLETE_COURSE_LESSON_MUTAION, {
    refetchQueries: [
      {
        query: GET_USER_ATTENDANCE,
        variables: {
          embaUserAttendanceSlug: userAttendanceSlug,
          embaCourseLessonSlug: lessonSlug,
        },
      },
    ],
  });

  const onEmbaCompleteCourseLesson = async () => {
    await embaCompleteCourseLessonMutation({
      variables: {
        input: {
          embaUserAttendanceSlug: userAttendanceSlug,
          embaCourseLessonSlug: lessonSlug,
        },
      },
    });
    trackAmplitudeLogEvent('lessonCompleted', {
      embaUserAttendanceSlug: userAttendanceSlug,
      lessonSlug: lessonSlug,
    });

    // role_playとgoal_dialogとreflectionの場合はリダイレクトしない containerで処理する
    if (
      lessonContentType === CourseLessonContentTypeEnum.RolePlay ||
      lessonContentType === CourseLessonContentTypeEnum.GoalDialog ||
      lessonContentType === CourseLessonContentTypeEnum.Reflection
    ) {
      return;
    }

    // その他のlessonTypeはcompleteページへ
    navigate({
      to: '/user-attendances/$userAttendanceSlug/lessons/$lessonSlug/complete',
      params: {
        userAttendanceSlug,
        lessonSlug,
      },
    });
  };

  return {
    onEmbaStartCourseLesson,
    startCourseLessonError,
    startCourseLessonLoading,
    onEmbaCompleteCourseLesson,
    completeCourseLessonError,
    completeCourseLessonLoading,
  };
};
